import React, { useMemo } from "react";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import DialogApuramentoHeader from "pages/comercial/components/DialogApuramentoHeader";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

export default function DialogIncentivoResultados({
  PaperComponent,
  open,
  onClose,
  incentivo,
  periodo,
  setPeriodo,
  resultados,
  loading,
  onConsultar,
  abrirResultadoDetalhe,
}) {
  const resultadosTabela = useMemo(() => {
    const vendedoresIdentificados = new Set();

    return (Array.isArray(resultados) ? resultados : []).map((linha) => {
      const refVendedor = String(linha?.refvendedor || "").trim();
      const idIncentivo = String(linha?.idincentivo || incentivo?.id || "");
      const chave = `${idIncentivo}|${refVendedor}`;

      const mostrarPagamento =
        refVendedor !== "" && refVendedor !== "__GLOBAL__" && !vendedoresIdentificados.has(chave);

      if (mostrarPagamento) {
        vendedoresIdentificados.add(chave);
      }

      return {
        ...linha,
        mostrar_pagamento_vendedor: mostrarPagamento,
      };
    });
  }, [resultados, incentivo?.id]);

  const columns = useMemo(
    () => [
      { Header: "Vendedor", accessor: "vendedor", width: 220 },
      // { Header: "Incentivo", accessor: "incentivo", width: 200 },
      { Header: "Grupo", accessor: "grupo", width: 150 },
      { Header: "Patamar", accessor: "patamar", width: 150 },
      {
        Header: "Faturação",
        accessor: "obtido",
        width: 300,
        Cell: ({ value }) => {
          if (value == null) return "";

          return String(value).replace(/(-?\d+(?:[.,]\d+)?)(?=\s+UN EQ\b)/g, (numero) => {
            const valor = Number(numero.replace(",", "."));

            return Number.isFinite(valor)
              ? valor.toLocaleString("pt-PT", {
                  maximumFractionDigits: 2,
                })
              : numero;
          });
        },
      },
      {
        Header: "Condições necessárias",
        accessor: "condicoes",
        width: 360,
      },
      {
        Header: "Prémio",
        accessor: "beneficio_descricao",
        width: 320,
      },

      {
        Header: "Estado",
        accessor: "atingido",
        width: 120,
        Cell: ({ value }) => (value ? "Atingido" : "Não atingido"),
      },
      {
        Header: "Prémio alcançado",
        accessor: "beneficio_obtido",
        width: 160,
        Cell: ({ value }) => value || "",
      },
      {
        Header: "A pagar (€)",
        accessor: "premio_total_vendedor_pagar",
        width: 150,
        Cell: ({ value, row }) => {
          if (!row.original.mostrar_pagamento_vendedor || value == null) {
            return "";
          }

          return Number(value).toLocaleString("pt-PT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        id: "detalhe",
        icon: <InfoOutlinedIcon fontSize="small" />,
        tooltip: "Ver clientes e faturas",
        color: "info",
        onClick: abrirResultadoDetalhe,
      },
    ],
    [abrirResultadoDetalhe]
  );

  const totalApurado = useMemo(() => {
    const linhas = Array.isArray(resultados) ? resultados : [];
    const vendedoresContabilizados = new Set();

    return linhas.reduce((total, linha) => {
      const refVendedor = String(linha?.refvendedor || "").trim();
      const idIncentivo = String(linha?.idincentivo || incentivo?.id || "");
      const chave = `${idIncentivo}|${refVendedor}`;

      if (refVendedor === "__GLOBAL__") {
        return total + Number(linha?.beneficio_obtido_numeric || 0);
      }

      if (vendedoresContabilizados.has(chave)) {
        return total;
      }

      vendedoresContabilizados.add(chave);

      if (linha?.premio_total_vendedor_pagar != null) {
        return total + Number(linha.premio_total_vendedor_pagar);
      }

      return (
        total +
        linhas
          .filter(
            (x) =>
              String(x?.idincentivo || incentivo?.id || "") === idIncentivo &&
              String(x?.refvendedor || "").trim() === refVendedor
          )
          .reduce((soma, x) => soma + Number(x?.beneficio_obtido_numeric || 0), 0)
      );
    }, 0);
  }, [resultados, incentivo?.id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperComponent={PaperComponent}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", xl: "1450px" },
          maxWidth: "100%",
          height: { xs: "94vh", md: "92vh" },
          maxHeight: { xs: "94vh", md: "92vh" },
          m: { xs: 1.5, md: 3 },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
        Resultados do incentivo
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 1.5, md: 3 },
          maxWidth: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <MDBox
          display="grid"
          gridTemplateRows="auto auto minmax(0, 1fr)"
          gap={2}
          mt={1}
          height="100%"
          minHeight={0}
        >
          <DialogApuramentoHeader
            tipo="Incentivo"
            id={incentivo?.id}
            nome={incentivo?.nome}
            fabricante={incentivo?.fabricante || incentivo?.reffabricante}
            periodo={periodo}
            setPeriodo={setPeriodo}
            total={totalApurado}
            loading={loading}
            onRecalcular={onConsultar}
          />

          <MDBox
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            flexWrap="wrap"
            gap={{ xs: 1, md: 3 }}
            alignItems={{ xs: "flex-start", md: "flex-start" }}
            px={2}
            py={1.5}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              backgroundColor: "#fafafa",
            }}
          >
            <MDBox minWidth={{ xs: "100%", md: 170 }}>
              <MDTypography variant="caption" color="text" display="block">
                Prémio mínimo por vendedor
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {incentivo?.premio_minimo_vendedor == null
                  ? "Sem mínimo definido"
                  : `${Number(incentivo.premio_minimo_vendedor).toLocaleString("pt-PT", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} €`}
              </MDTypography>
            </MDBox>

            <MDBox flex={1} minWidth={{ xs: "100%", md: 220 }}>
              <MDTypography variant="caption" color="text" display="block">
                Observações
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {incentivo?.obs?.trim() || "Sem observações"}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox minHeight={0} minWidth={0}>
            <ProjectTable
              tableKey="incentivos-resultados"
              version="v1"
              columns={columns}
              data={resultadosTabela}
              loading={loading}
              rowActions={rowActions}
              minWidth={900}
              maxHeight="40vh"
              emptyMessage="Sem resultados para apresentar."
              actionsPosition="left"
            />
          </MDBox>
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
